/**
 * Колокольчик в шапке (§18.4).
 *
 * Живёт рядом с переключателем темы и не размонтируется при переходах:
 * подписка на личный топик оформлена в AppShell, событие
 * `notification.created` сбрасывает тег Notification, и лента
 * перезапрашивается сама. Отдельного опроса по таймеру нет намеренно —
 * он и не нужен при живом сокете, и маскировал бы его обрыв.
 */

import { useNavigate } from "react-router-dom";

import { useIsPhone } from "@/shared/lib/media";
import { cn, Popover, SkeletonRows } from "@/shared/ui";
import { Bell } from "@/shared/ui/motionIcons";

import {
  type NotificationItem,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
  useNotificationsQuery,
} from "@taskflow/client-core/notifications";
import { groupByTask } from "./groupByTask";
import { NotificationGroup } from "./NotificationGroup";
import { NotificationRow } from "./NotificationRow";
import { useMissedNotificationsToast } from "./useMissedNotificationsToast";

/** Сколько строк показывать в колокольчике. Остальное — в истории:
 *  выпадающий список на полсотни строк перестаёт быть быстрым взглядом.
 *  Строка — задача целиком, поэтому событий
 *  запрашивается больше, чем строк: иначе три активные задачи
 *  съели бы всю выборку и колокольчик показал бы три строки. */
const PREVIEW_LIMIT = 12;
const FETCH_LIMIT = 40;

/** Выше этого числа счётчик показывает «99+»: трёхзначное число
 *  не помещается в кружок и ничего не добавляет к «очень много». */
const COUNTER_CAP = 99;

export function NotificationBell() {
  const navigate = useNavigate();
  const phone = useIsPhone();
  const { data, isLoading, fulfilledTimeStamp, refetch } = useNotificationsQuery({
    limit: FETCH_LIMIT,
  });
  useMissedNotificationsToast(data, fulfilledTimeStamp, () => void refetch());
  const [markRead] = useMarkNotificationsReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllNotificationsReadMutation();

  const items = data?.data ?? [];
  const entries = groupByTask(items).slice(0, PREVIEW_LIMIT);
  const unread = data?.unreadCount ?? 0;

  const open = (item: NotificationItem) => {
    if (!item.read) void markRead({ ids: [item.id] });
  };

  const bell = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        unread > 0 ? `Уведомления: ${unread} непрочитанных` : "Уведомления: все прочитаны"
      }
      title="Уведомления"
      className="relative inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
    >
      <Bell className="size-4" aria-hidden />
      {unread > 0 && (
        <span
          // aria-hidden: число уже названо в aria-label кнопки,
          // иначе скринридер произносит его дважды.
          aria-hidden
          className={cn(
            "absolute -top-0.5 -right-0.5 inline-flex min-w-4 items-center justify-center",
            "rounded-full bg-danger px-1 text-[10px] leading-4 font-bold text-white",
          )}
        >
          {unread > COUNTER_CAP ? `${COUNTER_CAP}+` : unread}
        </span>
      )}
    </button>
  );

  // На телефоне колокольчик ведёт на страницу уведомлений, а не открывает
  // выпадающий список (docs/08-mobile-roadmap.md, M6): лента на весь экран
  // удобнее листа поверх страницы, и у неё есть свой адрес.
  if (phone) return bell(() => void navigate("/notifications"));

  return (
    <Popover
      align="end"
      className="z-50 w-[26rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-md border border-line bg-raised shadow-md focus:outline-none"
      trigger={bell()}
    >
      {({ close }) => (
        <div className="flex max-h-[32rem] flex-col">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <p className="text-[13px] font-bold text-fg">Уведомления</p>
            <button
              type="button"
              disabled={unread === 0 || markingAll}
              onClick={() => void markAllRead()}
              className="text-[12px] font-semibold text-accent transition-colors hover:underline disabled:cursor-not-allowed disabled:text-faint disabled:no-underline"
            >
              Прочитать всё
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <SkeletonRows rows={4} className="p-3" />
            ) : items.length === 0 ? (
              <p className="px-4 py-10 text-center text-[13px] text-muted">
                Новых уведомлений нет
              </p>
            ) : (
              <ul>
                {entries.map((entry) => {
                  const onOpen = (row: NotificationItem) => {
                    open(row);
                    close();
                  };
                  return entry.kind === "group" ? (
                    <NotificationGroup key={entry.key} items={entry.items} onOpen={onOpen} />
                  ) : (
                    <NotificationRow key={entry.item.id} item={entry.item} compact onOpen={onOpen} />
                  );
                })}
              </ul>
            )}
          </div>

          <div className="shrink-0 border-t border-line px-4 py-2">
            <button
              type="button"
              onClick={() => {
                close();
                void navigate("/notifications");
              }}
              className="text-[12px] font-semibold text-accent transition-colors hover:underline"
            >
              Вся история уведомлений
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}
